import React, { useState, useEffect } from 'react';
import { Users, Phone, Shield, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useToast } from '../components/ui/use-toast';
import axios from 'axios';
import CreateUserModal from '../components/CreateUserModal';

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';

const ALL_PAGES = [
  { key: 'funded_deals', label: 'Funded Deals' },
  { key: 'submitted_deals', label: 'Submitted Deals' },
  { key: 'clients', label: 'Clients' },
  { key: 'companies', label: 'Companies' },
  { key: 'archived_deals', label: 'Archived Deals' },
  { key: 'portfolio', label: 'Portfolio Summary' },
  { key: 'admin_panel', label: 'Admin Panel' },
];

// Default page permissions per role. These are the pages a role is ALLOWED to view;
// all other pages auto-uncheck when the role is changed.
const ROLE_DEFAULT_PERMS = {
  admin:       { funded_deals: true, submitted_deals: true, clients: true, companies: true, archived_deals: true, portfolio: true, admin_panel: true },
  underwriter: { funded_deals: true, submitted_deals: true, clients: true, companies: true, archived_deals: true, portfolio: true, admin_panel: false },
  rep:         { funded_deals: true, submitted_deals: false, clients: false, companies: false, archived_deals: false, portfolio: true, admin_panel: false },
  syndicator:  { funded_deals: true, submitted_deals: false, clients: false, companies: false, archived_deals: false, portfolio: true, admin_panel: false },
  iso:         { funded_deals: true, submitted_deals: false, clients: false, companies: false, archived_deals: false, portfolio: true, admin_panel: false },
};

const StaffManagement = () => {
  const { toast } = useToast();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [expandedUser, setExpandedUser] = useState(null);
  const [actionLoading, setActionLoading] = useState('');
  // Local draft edits per user email so checkboxes reflect instant changes
  // without having to wait for the backend roundtrip.
  const [drafts, setDrafts] = useState({});

  useEffect(() => { loadUsers(); }, []);

  const loadUsers = async () => {
    try {
      const token = localStorage.getItem('mcaToken');
      const response = await axios.get(`${API_BASE}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data || []);
      setDrafts({}); // Reset drafts after a fresh load
    } catch (error) {
      console.error('Error loading users:', error);
    } finally {
      setLoading(false);
    }
  };

  const getEffective = (u) => {
    const d = drafts[u.email] || {};
    return {
      role: d.role ?? u.role,
      status: d.status ?? (u.status || 'active'),
      phone: d.phone ?? (u.phone || ''),
      page_permissions: d.page_permissions ?? (u.page_permissions || {}),
    };
  };

  const setDraft = (email, patch) => {
    setDrafts(prev => ({ ...prev, [email]: { ...(prev[email] || {}), ...patch } }));
  };

  const updateUser = async (email, updates) => {
    setActionLoading(email);
    try {
      const token = localStorage.getItem('mcaToken');
      await axios.put(
        `${API_BASE}/users/update-by-email`,
        { email, ...updates },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast({ title: 'Updated', description: `${email} updated` });
      await loadUsers();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Update failed', variant: 'destructive' });
    } finally {
      setActionLoading('');
    }
  };

  // Role dropdown change: immediately persist role AND swap page_permissions to the
  // role's defaults. Checkboxes below update in real time because of the drafts layer.
  const handleRoleChange = async (email, newRole) => {
    const defaults = ROLE_DEFAULT_PERMS[newRole] || {};
    setDraft(email, { role: newRole, page_permissions: defaults });
    await updateUser(email, { role: newRole, page_permissions: defaults });
  };

  const togglePermission = async (email, pageKey, checked) => {
    const current = getEffective(users.find(u => u.email === email)).page_permissions;
    const next = { ...current, [pageKey]: checked };
    setDraft(email, { page_permissions: next });
    await updateUser(email, { page_permissions: next });
  };

  const sendPasswordResetSMS = async (email) => {
    setActionLoading(email + '_sms');
    try {
      const token = localStorage.getItem('mcaToken');
      await axios.post(`${API_BASE}/auth/send-reset-sms`, { token, email });
      toast({ title: 'Sent', description: `Password reset link texted to ${email}` });
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to send SMS', variant: 'destructive' });
    } finally {
      setActionLoading('');
    }
  };

  const deleteUser = async (userId, email) => {
    if (!window.confirm(`Delete ${email}? This cannot be undone.`)) return;
    try {
      const token = localStorage.getItem('mcaToken');
      await axios.delete(`${API_BASE}/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ title: 'Deleted', description: `${email} removed` });
      loadUsers();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to delete user', variant: 'destructive' });
    }
  };

  const statusColor = (s) => {
    if (s === 'active') return 'bg-green-100 text-green-700';
    if (s === 'suspended') return 'bg-red-100 text-red-700';
    return 'bg-gray-100 text-gray-600';
  };

  const roleColor = (r) => {
    if (r === 'admin') return 'bg-red-100 text-red-700';
    if (r === 'syndicator' || r === 'rep') return 'bg-blue-100 text-blue-700';
    if (r === 'underwriter') return 'bg-purple-100 text-purple-700';
    return 'bg-gray-100 text-gray-700';
  };

  if (loading) return <div className="p-8 text-gray-500">Loading staff...</div>;

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-3">
            <Users size={28} className="text-orange-500" />
            Staff Management
          </h1>
          <p className="text-sm text-gray-500 mt-1">{users.length} team members</p>
        </div>
        <Button
          onClick={() => setShowCreateModal(true)}
          className="bg-orange-500 hover:bg-orange-600"
          data-testid="add-staff-btn"
        >
          + Add Staff Member
        </Button>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="text-left py-3 px-4 font-semibold">Name</th>
              <th className="text-left py-3 px-4 font-semibold">Email</th>
              <th className="text-left py-3 px-4 font-semibold">Phone</th>
              <th className="text-left py-3 px-4 font-semibold">Role</th>
              <th className="text-left py-3 px-4 font-semibold">Status</th>
              <th className="text-left py-3 px-4 font-semibold">2FA</th>
              <th className="text-left py-3 px-4 font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => {
              const isExpanded = expandedUser === u.email;
              const effective = getEffective(u);
              const userPerms = effective.page_permissions;
              return (
                <React.Fragment key={u._id || u.email}>
                  <tr className="border-b hover:bg-orange-50 transition">
                    <td className="py-3 px-4 font-medium">{u.name || 'N/A'}</td>
                    <td className="py-3 px-4 text-gray-600">{u.email}</td>
                    <td className="py-3 px-4 text-gray-600">{u.phone || 'N/A'}</td>
                    <td className="py-3 px-4">
                      <span className={`px-2 py-1 rounded text-xs ${roleColor(effective.role)}`}>{effective.role}</span>
                    </td>
                    <td className="py-3 px-4">
                      <span className={`px-2 py-1 rounded text-xs ${statusColor(u.status || 'active')}`}>
                        {u.status || 'active'}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      {u.two_fa_enabled ? (
                        <span className="px-2 py-1 rounded text-xs bg-green-100 text-green-700">ON</span>
                      ) : (
                        <span className="text-xs text-gray-400">Off</span>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex gap-2">
                        <button
                          onClick={() => setExpandedUser(isExpanded ? null : u.email)}
                          className="text-xs px-2 py-1 bg-gray-100 hover:bg-gray-200 rounded flex items-center gap-1"
                          data-testid={`manage-${u.email}`}
                        >
                          <Shield size={12} /> Manage
                          {isExpanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                        </button>
                        <button
                          onClick={() => sendPasswordResetSMS(u.email)}
                          disabled={!u.phone || actionLoading === u.email + '_sms'}
                          className="text-xs px-2 py-1 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded flex items-center gap-1 disabled:opacity-40"
                          data-testid={`sms-reset-${u.email}`}
                          title={u.phone ? 'Send password reset via SMS' : 'No phone number on file'}
                        >
                          <Phone size={12} />
                          {actionLoading === u.email + '_sms' ? 'Sending...' : 'Text Reset'}
                        </button>
                        <button
                          onClick={() => deleteUser(u._id, u.email)}
                          className="text-xs px-2 py-1 bg-red-100 hover:bg-red-200 text-red-700 rounded"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr className="bg-gray-50">
                      <td colSpan="7" className="p-4">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                          {/* Role */}
                          <div>
                            <label className="block text-sm font-medium mb-2">Role</label>
                            <select
                              value={effective.role}
                              onChange={(e) => handleRoleChange(u.email, e.target.value)}
                              className="w-full border rounded px-3 py-2 text-sm"
                              disabled={actionLoading === u.email}
                              data-testid={`role-select-${u.email}`}
                            >
                              <option value="admin">Admin</option>
                              <option value="underwriter">Underwriter</option>
                              <option value="rep">Representative</option>
                              <option value="syndicator">Syndicator</option>
                              <option value="iso">ISO</option>
                            </select>
                            <p className="text-[11px] text-gray-500 mt-1">
                              Changing role auto-applies default page permissions for that role
                            </p>
                          </div>

                          {/* Status */}
                          <div>
                            <label className="block text-sm font-medium mb-2">Status</label>
                            <select
                              value={effective.status}
                              onChange={(e) => {
                                setDraft(u.email, { status: e.target.value });
                                updateUser(u.email, { status: e.target.value });
                              }}
                              className="w-full border rounded px-3 py-2 text-sm"
                              disabled={actionLoading === u.email}
                              data-testid={`status-select-${u.email}`}
                            >
                              <option value="active">Active</option>
                              <option value="suspended">Suspended</option>
                              <option value="inactive">Inactive</option>
                            </select>
                          </div>

                          {/* Phone */}
                          <div>
                            <label className="block text-sm font-medium mb-2">Phone</label>
                            <div className="flex gap-2">
                              <Input
                                type="tel"
                                defaultValue={u.phone || ''}
                                placeholder="+1 555 123 4567"
                                id={`phone-${u.email}`}
                                className="text-sm"
                              />
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  const val = document.getElementById(`phone-${u.email}`).value;
                                  updateUser(u.email, { phone: val });
                                }}
                              >
                                Save
                              </Button>
                            </div>
                          </div>

                          {/* Change Password */}
                          <div className="md:col-span-3">
                            <label className="block text-sm font-medium mb-2">Change Password</label>
                            <div className="flex gap-2 max-w-md">
                              <Input
                                type="password"
                                placeholder="Enter new password"
                                id={`pwd-${u.email}`}
                                className="text-sm"
                              />
                              <Button
                                size="sm"
                                className="bg-orange-500 hover:bg-orange-600"
                                onClick={async () => {
                                  const pwd = document.getElementById(`pwd-${u.email}`).value;
                                  if (!pwd || pwd.length < 6) {
                                    toast({ title: 'Error', description: 'Password must be at least 6 characters', variant: 'destructive' });
                                    return;
                                  }
                                  try {
                                    const token = localStorage.getItem('mcaToken');
                                    await axios.post(`${API_BASE}/auth/reset-password`, {
                                      token, email: u.email, new_password: pwd
                                    });
                                    toast({ title: 'Success', description: `Password changed for ${u.email}` });
                                    document.getElementById(`pwd-${u.email}`).value = '';
                                  } catch (err) {
                                    toast({ title: 'Error', description: err.response?.data?.detail || 'Failed', variant: 'destructive' });
                                  }
                                }}
                              >
                                Set Password
                              </Button>
                            </div>
                          </div>

                          {/* Page Permissions */}
                          <div className="md:col-span-3">
                            <label className="block text-sm font-medium mb-2">Page Permissions</label>
                            <div className="flex flex-wrap gap-3">
                              {ALL_PAGES.map((page) => {
                                // Legacy default: missing key means allowed (true).
                                // Once a role is picked, ROLE_DEFAULT_PERMS populates every key explicitly.
                                const isChecked = userPerms[page.key] === undefined
                                  ? true
                                  : !!userPerms[page.key];
                                return (
                                  <label
                                    key={page.key}
                                    className="flex items-center gap-2 bg-white border rounded px-3 py-2 cursor-pointer hover:bg-orange-50 text-sm"
                                    data-testid={`perm-${page.key}-${u.email}`}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={isChecked}
                                      onChange={(e) => togglePermission(u.email, page.key, e.target.checked)}
                                      disabled={actionLoading === u.email}
                                      className="w-4 h-4"
                                    />
                                    {page.label}
                                  </label>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {showCreateModal && (
        <CreateUserModal
          onClose={() => {
            setShowCreateModal(false);
            loadUsers();
          }}
        />
      )}
    </div>
  );
};

export default StaffManagement;
